import { AlertTriangle, Bot, RotateCw, Trash2, X } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api } from "@/api/client";
import {
  useDeleteSession,
  useEvents,
  useInterruptAgent,
  useSendMessage,
  useSessionInfo,
  useStartAgent,
  useWakeDesk,
} from "@/api/hooks";
import {
  type EventEntry,
  deriveSessionState,
  isSessionUnresumable,
  timeAgo,
} from "@/api/types";
import {
  DEFAULT_MODEL,
  findModel,
  type Harness,
  ModelSelector,
  type ModelOption,
} from "./model-selector";
import { SessionComposer } from "./session-composer";
import { SessionMessage } from "./session-message";
import { type ClientPhase, SessionStatus } from "./session-status";

interface SessionTileProps {
  deskId: string;
  sessionId: string | null;
  canAgent: boolean; // desk is active enough to accept agent ops
  onSessionCreated: (sessionId: string) => void;
  onRequestNewSession?: () => void;
  onClose?: () => void;
  onOpenFile: (path: string, name: string) => void;
  focused?: boolean;
  onFocus?: () => void;
}

type OptimisticMsg = { id: string; prompt: string };

export function SessionTile({
  deskId,
  sessionId,
  canAgent,
  onSessionCreated,
  onRequestNewSession,
  onClose,
  onOpenFile,
  focused = true,
  onFocus,
}: SessionTileProps) {
  const [input, setInput] = useState("");
  const [clientPhase, setClientPhase] = useState<ClientPhase>("idle");
  const [optimistic, setOptimistic] = useState<OptimisticMsg | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [interruptError, setInterruptError] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] =
    useState<ModelOption>(DEFAULT_MODEL);

  const { data: session } = useSessionInfo(deskId, sessionId);

  // Sync model selector to the session's model when switching sessions
  useEffect(() => {
    if (session?.model && session.model !== "unknown") {
      const found = findModel(session.model);
      if (found) setSelectedModel(found);
    }
  }, [session?.model]);

  const lockedHarness: Harness | null =
    session?.invocation === "claude" || session?.invocation === "pi-shim"
      ? (session.invocation as Harness)
      : null;
  const serverState = session ? deriveSessionState(session) : null;
  const sessionActive =
    serverState === "starting" ||
    serverState === "running" ||
    serverState === "thinking";

  const { data: events } = useEvents(deskId, sessionId, sessionActive);

  const startAgent = useStartAgent(deskId);
  const sendMessage = useSendMessage(deskId);
  const interrupt = useInterruptAgent(deskId);
  const deleteSession = useDeleteSession(deskId);
  const wakeMutation = useWakeDesk();

  // Clear optimistic bubble once the server confirms the prompt is in events.
  // Matching on prompt text handles the narrow race where mutation resolves
  // before the events poll picks up the user_prompt event.
  useEffect(() => {
    if (!optimistic || !events) return;
    const matched = events.some(
      (e) =>
        e.type === "user_prompt" && (e.prompt as string) === optimistic.prompt,
    );
    if (matched) setOptimistic(null);
  }, [events, optimistic]);

  // When the first event of ANY kind arrives for an awaiting session,
  // transition to idle (server session state takes over from here).
  useEffect(() => {
    if (clientPhase !== "awaiting_first_event") return;
    if ((events?.length ?? 0) > 0 || sessionActive) {
      setClientPhase("idle");
    }
  }, [clientPhase, events, sessionActive]);

  // --- Send flow ---
  const handleSend = async () => {
    const prompt = input.trim();
    if (!prompt) return;
    setSendError(null);
    setInput("");
    setOptimistic({ id: `${Date.now()}`, prompt });

    try {
      // Wake the desk first if it's not ready yet
      if (!canAgent) {
        setClientPhase("waking_desk");
        try {
          await wakeMutation.mutateAsync(deskId);
        } catch {
          await new Promise((r) => setTimeout(r, 1500));
          await wakeMutation.mutateAsync(deskId);
        }
        // Poll desk status up to 8s for "ready"
        for (let i = 0; i < 8; i++) {
          await new Promise((r) => setTimeout(r, 1000));
          try {
            const d = await api.getDesk(deskId);
            if (d.desk.status === "ready") break;
          } catch {
            /* keep polling */
          }
        }
      }

      setClientPhase("sending");

      if (sessionId) {
        await sendMessage.mutateAsync({
          sessionId,
          prompt,
          model: selectedModel.id,
        });
      } else {
        const result = await startAgent.mutateAsync({
          prompt,
          invocation: selectedModel.harness,
          model: selectedModel.id,
        });
        onSessionCreated(result.sessionId);
      }

      // Mutation succeeded but the agent may not have produced its first event
      // yet; keep showing a "Starting…" state until the first event lands.
      setClientPhase("awaiting_first_event");
    } catch (e) {
      const raw = e instanceof Error ? e.message : "Failed to send message";
      setSendError(raw);
      setClientPhase("idle");
      // Restore the user's prompt so they don't have to retype it.
      setInput(prompt);
      setOptimistic(null);
    }
  };

  const handleInterrupt = () => {
    if (!sessionId) return;
    setInterruptError(null);
    interrupt.mutate(sessionId, {
      onError: (e) =>
        setInterruptError(e instanceof Error ? e.message : "Interrupt failed"),
    });
  };

  const handleDelete = () => {
    if (!sessionId) return onClose?.();
    if (!confirm("Delete this session? Conversation history will be removed."))
      return;
    deleteSession.mutate(sessionId, {
      onSuccess: () => onClose?.(),
    });
  };

  // --- Auto-scroll to bottom on new messages ---
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastEventCountRef = useRef(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: clientPhase is included intentionally — phase transitions ("thinking" -> "running" etc.) shift layout and we want to re-measure scroll position even though the value isn't read in the effect body.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const count = (events?.length ?? 0) + (optimistic ? 1 : 0);
    // Auto-scroll if user is near the bottom OR new events arrived
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    if (isNearBottom || count > lastEventCountRef.current) {
      el.scrollTop = el.scrollHeight;
    }
    lastEventCountRef.current = count;
  }, [events, optimistic, clientPhase]);

  // For new (not yet started) sessions, mirror the selected model in the
  // header so the user sees what clicking Send will actually launch.
  const invocation = session?.invocation ?? selectedModel.harness;
  const model = session?.model ?? selectedModel.id;
  const cost = session?.totalCost;
  const createdAgo = session ? timeAgo(session.createdAt) : null;

  const eventsToRender = useMemo(() => {
    if (!events) return [];
    // Deduplicate: if the optimistic prompt already shows up as user_prompt, hide optimistic
    return events.filter((e) => shouldRender(e));
  }, [events]);

  const isEmpty =
    !events?.length && !optimistic && clientPhase === "idle" && !sessionId;

  const unresumable = session ? isSessionUnresumable(session) : false;

  return (
    <div
      className="flex flex-col h-full border rounded-lg overflow-hidden transition-shadow"
      style={{
        background: "var(--surface)",
        borderColor: focused ? "var(--border)" : "var(--blt)",
        boxShadow: focused ? "0 2px 12px rgba(26, 18, 16, 0.05)" : "none",
        minHeight: 0,
      }}
      onClick={onFocus}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-3 py-2 border-b"
        style={{
          borderColor: "var(--blt)",
          flexShrink: 0,
          background: "var(--white)",
        }}
      >
        <Bot size={13} style={{ color: "var(--t3)" }} />
        <div className="flex items-baseline gap-1.5 min-w-0 flex-1">
          <span
            className="text-[12px] font-medium truncate"
            style={{
              fontFamily: "var(--font-mono)",
              color: "var(--t1)",
            }}
            title={`${invocation} · ${model}`}
          >
            {invocation}
          </span>
          <span
            className="text-[11px] truncate"
            style={{
              fontFamily: "var(--font-mono)",
              color: "var(--t4)",
            }}
          >
            {shortModelName(model)}
          </span>
          {createdAgo && (
            <span
              className="text-[11px] shrink-0"
              style={{ color: "var(--t4)" }}
            >
              · {createdAgo}
            </span>
          )}
          {cost != null && cost > 0 && (
            <span
              className="text-[11px] tabular-nums ml-auto shrink-0"
              style={{
                fontFamily: "var(--font-mono)",
                color: "var(--t3)",
              }}
            >
              ${cost.toFixed(3)}
            </span>
          )}
        </div>
        {sessionId && (
          <button
            type="button"
            onClick={handleDelete}
            className="p-1 rounded hover:bg-[var(--hover)]"
            style={{ color: "var(--t4)" }}
            title="Delete session"
          >
            <Trash2 size={12} />
          </button>
        )}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-[var(--hover)]"
            style={{ color: "var(--t3)" }}
            title="Close tile"
          >
            <X size={12} />
          </button>
        )}
      </div>

      {/* Status band — the critical state clarity line */}
      <div
        className="px-3 py-1.5 border-b flex items-center justify-between gap-2"
        style={{
          borderColor: "var(--blt)",
          background: "var(--surface)",
          flexShrink: 0,
        }}
      >
        <SessionStatus
          session={session ?? null}
          clientPhase={clientPhase}
          eventCount={events?.length ?? null}
        />
        {serverState === "stalled" && sessionId && (
          <button
            type="button"
            onClick={handleInterrupt}
            className="text-[11px] px-2 py-0.5 rounded flex items-center gap-1"
            style={{
              background: "color-mix(in srgb, var(--warm) 12%, transparent)",
              color: "var(--warm)",
              border:
                "1px solid color-mix(in srgb, var(--warm) 30%, transparent)",
            }}
            title="Interrupt the stalled agent"
          >
            <RotateCw size={10} />
            Reset
          </button>
        )}
      </div>

      {unresumable && (
        <div
          className="flex items-start gap-2 px-3 py-2 border-b"
          style={{
            background: "color-mix(in srgb, var(--warm) 8%, transparent)",
            borderColor: "var(--blt)",
          }}
        >
          <AlertTriangle
            size={13}
            className="mt-0.5 shrink-0"
            style={{ color: "var(--warm)" }}
          />
          <div className="flex-1 min-w-0">
            <p
              className="text-[12px] font-medium"
              style={{ color: "var(--t1)" }}
            >
              This session can't be resumed
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: "var(--t3)" }}>
              The sprite went cold before the agent produced a session id. Start
              a new session to continue.
            </p>
          </div>
          {onRequestNewSession && (
            <button
              type="button"
              onClick={onRequestNewSession}
              className="text-[11px] px-2 py-0.5 rounded shrink-0"
              style={{ background: "var(--accent)", color: "var(--white)" }}
            >
              New session
            </button>
          )}
        </div>
      )}

      {interruptError && (
        <div
          className="flex items-center justify-between gap-2 px-3 py-1.5 border-b"
          style={{
            background: "color-mix(in srgb, var(--err) 6%, transparent)",
            borderColor: "var(--blt)",
          }}
        >
          <p className="text-[11px]" style={{ color: "var(--err)" }}>
            Couldn't interrupt: {interruptError}
          </p>
          <button
            type="button"
            onClick={() => setInterruptError(null)}
            className="text-[11px]"
            style={{ color: "var(--t3)" }}
          >
            dismiss
          </button>
        </div>
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-3 space-y-3"
        style={{ minHeight: 0 }}
      >
        {isEmpty && (
          <div className="h-full flex items-center justify-center text-center">
            <div className="space-y-2">
              <img
                src="/frogs/sleeping.gif"
                alt=""
                className="w-10 h-10 mx-auto opacity-40"
              />
              <p
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "var(--text-base)",
                  color: "var(--t3)",
                }}
              >
                New conversation
              </p>
              <p
                className="text-[11px] max-w-[220px] mx-auto"
                style={{ color: "var(--t4)" }}
              >
                Type a prompt below. The desk will wake automatically if needed.
              </p>
            </div>
          </div>
        )}

        {eventsToRender.map((event, i) => (
          <SessionMessage
            key={`${event.type}-${i}`}
            event={event}
            onOpenFile={onOpenFile}
          />
        ))}

        {optimistic && (
          <div
            className="flex items-start gap-2 animate-fade-in"
            style={{
              animationDuration: "180ms",
              opacity: 0.75,
            }}
          >
            <div
              className="rounded-lg px-3 py-2 max-w-full"
              style={{
                background: "color-mix(in srgb, var(--accent) 4%, transparent)",
                border:
                  "1px dashed color-mix(in srgb, var(--accent) 25%, transparent)",
                color: "var(--t2)",
                fontSize: "13.5px",
                lineHeight: 1.5,
                fontFamily: "var(--font-body)",
              }}
            >
              {optimistic.prompt.slice(0, 2000)}
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div
        className="px-3 pb-3 pt-2 border-t space-y-1.5"
        style={{
          borderColor: "var(--blt)",
          background: "var(--surface)",
          flexShrink: 0,
        }}
      >
        {!sessionActive && clientPhase === "idle" && (
          <div className="flex items-center gap-2">
            <ModelSelector
              value={selectedModel}
              onChange={setSelectedModel}
              lockedHarness={lockedHarness}
            />
          </div>
        )}
        <SessionComposer
          value={input}
          onChange={setInput}
          onSend={handleSend}
          onInterrupt={handleInterrupt}
          clientPhase={clientPhase}
          sessionActive={sessionActive}
          canInterrupt={!!sessionId && sessionActive}
          error={sendError}
          onDismissError={() => setSendError(null)}
          deskId={deskId}
        />
      </div>
    </div>
  );
}

/**
 * "google/gemini-flash-lite-latest" → "gemini-flash-lite"
 * "anthropic/claude-sonnet-4-6" → "claude-sonnet-4-6"
 * "sonnet" → "sonnet"
 * Keeps tile headers legible in the 2-col and 3-col grid layouts.
 */
function shortModelName(model: string): string {
  const segment = model.split("/").pop() ?? model;
  return segment.replace(/-latest$/, "").replace(/-preview$/, "");
}

function shouldRender(event: EventEntry): boolean {
  if (event.type === "system") return false;
  if (event.type === "timeline_divergence") return false;
  // Hide successful tool_results (they're implicit in the following assistant
  // message's content) — only show error ones via the message component.
  if (event.type === "user" && event.is_error !== true) return false;
  return true;
}
